<template>
  <b-modal
    id="modal-schedule"
    ref="modalSchedule"
    title="Batch Schedule"
    size="lg"
    hide-footer
    @hidden="resetModal">
    <div v-if="selectedBatch">
      <!-- Batch Info Header -->
      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 class="mb-2">{{ selectedCourse?.title }} - {{ formatBatchLabel(selectedBatch) }}</h5>
            <div class="d-flex gap-2 align-items-center">
              <span class="badge" :class="getBatchStatusClass(selectedBatch.batch_status)">
                {{ selectedBatch.batch_status }}
              </span>
              <span class="text-muted">•</span>
              <small class="text-muted">
                Quota: {{ selectedBatch.quota }} | Enrolled: {{ selectedBatch.enrolled }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Schedule Information -->
      <div class="schedule-container">
        <div v-if="!selectedBatch.schedules || selectedBatch.schedules.length === 0" class="text-center text-muted py-4">
          <i class="fe fe-calendar" style="font-size: 3rem; color: #dee2e6;"></i>
          <p class="mt-3">No schedules available for this batch</p>
        </div>

        <div v-else>
          <div v-for="(schedule, index) in selectedBatch.schedules" :key="index" class="schedule-card mb-3">
            <div class="d-flex">
              <div class="schedule-date">
                <div class="date-number">{{ formatDay(schedule.date) }}</div>
                <div class="date-month">{{ formatMonth(schedule.date) }}</div>
              </div>
              <div class="schedule-details flex-grow-1">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h6 class="mb-0">Session {{ index + 1 }}</h6>
                  <div class="d-flex gap-2">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="viewAttendanceReport(schedule)"
                      title="View Attendance Report">
                      <i class="fe fe-eye"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-outline-danger"
                      @click="downloadAttendanceReport(schedule)"
                      :disabled="downloadingReportId === schedule.id"
                      title="Download Attendance Report">
                      <i class="fe fe-download"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-primary"
                      @click="generateQRCode(schedule)"
                      :disabled="generatingQR">
                      <i class="fe fe-aperture me-1"></i>
                      {{ generatingQR ? 'Generating...' : 'Generate QR' }}
                    </button>
                  </div>
                </div>
                <div class="schedule-info">
                  <div class="info-row">
                    <i class="fe fe-clock text-muted"></i>
                    <span>{{ formatTime(schedule.start_time) }} - {{ formatTime(schedule.end_time) }}</span>
                  </div>
                  <div class="info-row" v-if="schedule.location">
                    <i class="fe fe-map-pin text-muted"></i>
                    <span>{{ schedule.location }}</span>
                  </div>
                  <div class="info-row" v-if="schedule.instructor">
                    <i class="fe fe-user text-muted"></i>
                    <span>{{ schedule.instructor }}</span>
                  </div>
                  <div class="info-row" v-if="schedule.topic">
                    <i class="fe fe-book text-muted"></i>
                    <span>{{ schedule.topic }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="mt-4 d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal">
          Close
        </button>
      </div>
    </div>

    <!-- QR Code Modal -->
    <b-modal
      id="modal-qr-code"
      ref="modalQRCode"
      title="Attendance QR Code"
      size="md"
      hide-footer
      centered
      @shown="renderQRCode">
      <div v-if="qrCodeUrl" class="text-center">
        <div class="mb-3">
          <h6>{{ selectedCourse?.title }}</h6>
          <p class="text-muted mb-0">Session - {{ formatDay(currentSchedule?.date) }} {{ formatMonth(currentSchedule?.date) }}</p>
        </div>

        <!-- QR Code Display -->
        <div class="qr-code-wrapper mb-3" id="qr-code-container">
          <canvas ref="qrCanvas"></canvas>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex gap-2 justify-content-center">
          <button
            class="btn btn-success"
            @click="downloadQRCode">
            <i class="fe fe-download me-1"></i>
            Download
          </button>
          <button
            class="btn btn-info"
            @click="printQRCode">
            <i class="fe fe-printer me-1"></i>
            Print
          </button>
          <button
            class="btn btn-secondary"
            @click="$bvModal.hide('modal-qr-code')">
            Close
          </button>
        </div>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import QRCode from 'qrcode';
import { formatBatchLabel } from '@/utils/batchIdentity';

export default {
  name: 'ModalSchedule',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    selectedBatch: {
      type: Object,
      default: null
    },
    batchIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      generatingQR: false,
      downloadingReportId: null,
      qrCodeUrl: null,
      currentSchedule: null
    };
  },
  methods: {
    formatBatchLabel,
    resetModal() {
      // Reset any local state if needed
      this.qrCodeUrl = null;
      this.currentSchedule = null;
    },

    closeModal() {
      this.$refs.modalSchedule.hide();
    },

    async generateQRCode(schedule) {
      if (this.generatingQR) return;

      this.generatingQR = true;
      try {
        // Call API to generate signed URL with scheduleId
        const response = await this.$store.dispatch('mitraAttendance/generateSignedUrl',
          schedule.id || schedule.training_event_id
        );

        // Get the signed URL from response.data.signed_url
        const signedUrl = response.data?.signed_url;

        if (!signedUrl) {
          throw new Error('No signed URL returned from server');
        }

        // Store the URL and schedule info
        this.qrCodeUrl = signedUrl;
        this.currentSchedule = schedule;

        // Show QR modal (renderQRCode will be called by @shown event)
        this.$bvModal.show('modal-qr-code');
      } catch (error) {
        console.error('Error generating signed URL:', error);
        this.$swal({
          title: 'Error',
          text: error.response?.data?.message || error.message || 'QR code belum berhasil dibuat',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.generatingQR = false;
      }
    },

    viewAttendanceReport(schedule) {
      if (!schedule?.id) return;
      this.$emit('view-schedule-attendance', {
        scheduleId: schedule.id,
        trainingEventId: this.selectedBatch?.training_event_id
      });
    },

    async downloadAttendanceReport(schedule) {
      if (!schedule?.id || this.downloadingReportId === schedule.id) return;

      this.downloadingReportId = schedule.id;
      try {
        const response = await this.$store.dispatch('attendanceReport/downloadScheduleReport', schedule.id);
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `attendance-${this.selectedCourse?.title || 'report'}-${this.formatDay(schedule.date)}-${this.formatMonth(schedule.date)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$swal({
          title: 'Success',
          text: 'Laporan absensi berhasil diunduh',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2c7be5'
        });
      } catch (error) {
        console.error('Error downloading attendance report:', error);
        this.$swal({
          title: 'Error',
          text: 'Laporan absensi belum berhasil diunduh. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.downloadingReportId = null;
      }
    },

    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    },

    formatDay(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.getDate();
    },

    formatMonth(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      return months[date.getMonth()];
    },

    formatTime(timeString) {
      if (!timeString) return '-';
      // Extract HH:MM from formats like "15:35:00+00" or "15:35:00"
      const match = timeString.match(/^(\d{2}):(\d{2})/);
      if (match) {
        return `${match[1]}:${match[2]}`;
      }
      return timeString;
    },

    async renderQRCode() {
      console.log('🎨 renderQRCode called');
      console.log('📌 qrCanvas ref:', this.$refs.qrCanvas);
      console.log('🔗 qrCodeUrl:', this.qrCodeUrl);

      if (!this.$refs.qrCanvas) {
        console.error('❌ Canvas ref not found');
        return;
      }

      if (!this.qrCodeUrl) {
        console.error('❌ QR code URL is empty');
        return;
      }

      try {
        console.log('✅ Generating QR code...');
        await QRCode.toCanvas(this.$refs.qrCanvas, this.qrCodeUrl, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        console.log('✅ QR code rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering QR code:', error);
        this.$swal({
          title: 'Error',
          text: 'QR code belum berhasil ditampilkan: ' + error.message,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },

    downloadQRCode() {
      const canvas = document.querySelector('#qr-code-container canvas');
      if (!canvas) return;

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const fileName = `QR_${this.selectedCourse?.title || 'Attendance'}_${this.formatDay(this.currentSchedule?.date)}_${this.formatMonth(this.currentSchedule?.date)}.png`;
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    },

    printQRCode() {
      const canvas = document.querySelector('#qr-code-container canvas');
      if (!canvas) return;

      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      const dataUrl = canvas.toDataURL();

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print QR Code</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                margin: 20px;
                font-family: Arial, sans-serif;
              }
              h3 { margin: 10px 0; }
              p { margin: 5px 0; color: #666; }
              img { margin: 20px 0; }
            </style>
          </head>
          <body>
            <h3>${this.selectedCourse?.title || 'Attendance'}</h3>
            <p>Session - ${this.formatDay(this.currentSchedule?.date)} ${this.formatMonth(this.currentSchedule?.date)}</p>
            <img src="${dataUrl}" alt="QR Code" />
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      // Wait for image to load before printing
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  }
};
</script>

<style scoped>
.schedule-container {
  max-height: 500px;
  overflow-y: auto;
}

.schedule-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
}

.schedule-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: #dee2e6;
}

.schedule-date {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  color: white;
  margin-right: 1rem;
  min-width: 70px;
}

.date-number {
  font-size: 1.75rem;
  font-weight: bold;
  line-height: 1;
}

.date-month {
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.25rem;
  letter-spacing: 1px;
}

.schedule-details {
  padding: 0.25rem 0;
}

.schedule-info {
  margin-top: 0.75rem;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row i {
  width: 20px;
  margin-right: 0.5rem;
  font-size: 0.875rem;
}

.badge-soft-primary {
  color: #2c7be5;
  background-color: rgba(44, 123, 229, 0.1);
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}

.gap-2 {
  gap: 0.5rem;
}

.qr-code-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.me-1 {
  margin-right: 0.25rem;
}
</style>
